/**
 * Per-theme instrument palettes: the same gameplay gesture ("chime", "sparkle", "scuff") gets a
 * timbre native to the map. Sky = kalimba, glass bells, grass. Dunes = handpan, oud/qanun,
 * zills, wood, sand.
 */
import { bloomKick, dum } from './instruments/drums';
import { glassBell, glassTick, handpan, kalimba, twinkle, woodTick, zill } from './instruments/mallets';
import { rustle, sandScuff } from './instruments/noisefx';
import { oud, qanun } from './instruments/pluck';
import type { NoteOpts, Synth } from './instruments/synth';
import { DUNES, SKY, degreeMidi, type ThemeDef } from './themes';

export interface Palette {
  readonly theme: ThemeDef;
  /** Bright tuned chime (landings, UI select). */
  chime(t: number, midi: number, vel: number, o?: NoteOpts): void;
  /** Long ringing bell (milestones, combo wrap). */
  bell(t: number, midi: number, vel: number): void;
  /** Melodic pluck. */
  pluck(t: number, midi: number, vel: number, o?: NoteOpts): void;
  /** Bright run note (fanfares): one consistent sparkly timbre across the register. */
  flourish(t: number, midi: number, vel: number): void;
  /** UI tick. */
  tick(t: number, midi: number, vel: number, muted: boolean): void;
  /** Shimmer / sparkle. */
  sparkle(t: number, vel: number): void;
  /** Tiny high "tink" (close calls). */
  tink(t: number, pan: number): void;
  /** Ground scuff (rough landings). */
  scuff(t: number, vel: number, pan?: number): void;
  /** Low percussive accent. */
  accent(t: number, vel: number): void;
}

class SkyPalette implements Palette {
  readonly theme = SKY;
  constructor(private readonly s: Synth) {}

  chime(t: number, midi: number, vel: number, o: NoteOpts = {}): void {
    kalimba(this.s, t, midi, vel, { send: 0.4, ...o });
    glassBell(this.s, t, midi, vel * 0.32, { send: 0.5, decay: 0.7, pan: o.pan, muted: o.muted });
  }

  bell(t: number, midi: number, vel: number): void {
    glassBell(this.s, t, midi, vel, { send: 0.55, decay: 1.4 });
    kalimba(this.s, t, midi - 12, vel * 0.35, { send: 0.4 });
  }

  pluck(t: number, midi: number, vel: number, o: NoteOpts = {}): void {
    kalimba(this.s, t, midi, vel, { send: 0.35, ...o });
  }

  flourish(t: number, midi: number, vel: number): void {
    kalimba(this.s, t, midi, vel, { send: 0.45 });
    if (midi >= 86) glassBell(this.s, t, midi, vel * 0.25, { send: 0.5, decay: 0.5 });
  }

  tick(t: number, midi: number, vel: number, muted: boolean): void {
    glassTick(this.s, t, midi, vel, { send: 0.12, muted });
  }

  sparkle(t: number, vel: number): void {
    const rng = this.s.kit.rng;
    const notes: number[] = [];
    let d = rng.int(0, 2);
    for (let i = 0; i < 5; i++) {
      notes.push(degreeMidi(SKY, d, SKY.chimeRoot + 12));
      d += rng.int(1, 2);
    }
    twinkle(this.s, t, vel, notes);
  }

  tink(t: number, pan: number): void {
    const rng = this.s.kit.rng;
    glassTick(this.s, t, degreeMidi(SKY, rng.int(0, 4), SKY.chimeRoot + 24), 0.45, { pan, send: 0.3 });
  }

  scuff(t: number, vel: number, pan = 0): void {
    rustle(this.s, t, { dur: 0.16 + 0.18 * vel, peak: 0.22 * vel, freq: 3000, q: 0.7, rate: 1.4, pan });
  }

  accent(t: number, vel: number): void {
    bloomKick(this.s, t, vel * 0.8);
  }
}

class DunesPalette implements Palette {
  readonly theme = DUNES;
  constructor(private readonly s: Synth) {}

  chime(t: number, midi: number, vel: number, o: NoteOpts = {}): void {
    handpan(this.s, t, midi, vel, { send: 0.35, ...o });
    qanun(this.s, t, midi, vel * 0.22, { pan: o.pan, muted: o.muted });
  }

  bell(t: number, midi: number, vel: number): void {
    zill(this.s, t, vel * 1.2);
    handpan(this.s, t, midi - 12, vel * 0.85, { send: 0.4 });
  }

  pluck(t: number, midi: number, vel: number, o: NoteOpts = {}): void {
    if (midi >= 72) qanun(this.s, t, midi, vel, o);
    else oud(this.s, t, midi, vel, o);
  }

  flourish(t: number, midi: number, vel: number): void {
    qanun(this.s, t, midi, vel, { send: 0.35 });
  }

  tick(t: number, midi: number, vel: number, muted: boolean): void {
    woodTick(this.s, t, midi, vel, { send: 0.08, muted });
  }

  sparkle(t: number, vel: number): void {
    const rng = this.s.kit.rng;
    const p = rng.range(0.97, 1.04);
    zill(this.s, t, vel * 0.7, { pitch: p, pan: -0.25 });
    zill(this.s, t + rng.range(0.04, 0.07), vel * 0.4, { pitch: p * 1.06, pan: 0.25 });
  }

  tink(t: number, pan: number): void {
    zill(this.s, t, 0.35, { pitch: 1.35, decay: 0.35, pan });
  }

  scuff(t: number, vel: number, pan = 0): void {
    sandScuff(this.s, t, 0.2 + 0.2 * vel, 0.25 * vel, pan);
  }

  accent(t: number, vel: number): void {
    dum(this.s, t, vel);
  }
}

export function createPalette(theme: ThemeDef, s: Synth): Palette {
  return theme.id === 'dunes' ? new DunesPalette(s) : new SkyPalette(s);
}
